import { queryOptions, useQuery, type UseQueryResult } from '@tanstack/react-query';
import {
  NO_FACET_MATCH,
  boardTimelineQuerySchema,
  boardTimelineSchema,
  ulidSchema,
  type ApiError,
  type BoardTimeline,
  type BoardTimelineQuery,
  type TimelineCardEntry,
  type TimelineGroupBy,
  type TimelineLane,
} from '@molt/shared';
import { EMPTY_BOARD_FILTER, type BoardFilterSelection } from './board-filter';
import {
  addCalendarDays,
  calendarDateFromKey,
  calendarDateKey,
  diffCalendarDays,
  dueDateForCalendarDate,
  replaceTimestampDate,
  shiftTimestampByCalendarDays,
  timestampDateKey,
} from './board-calendar';
import { get } from './api';
import type { LocaleFormattingTranslate } from './locale-formatting';
import { queryKeys } from './query-keys';

export type BoardTimelineZoom = 'day' | 'week' | 'month' | 'quarter' | 'year';

export interface BoardTimelineQueryInput {
  readonly from: string;
  readonly to: string;
  readonly groupBy?: TimelineGroupBy | undefined;
  readonly filter?: BoardFilterSelection | undefined;
}

/**
 * ⚠ NO `label` AND NO `shortLabel`, and their absence is the ruling FE-80.1
 * T15-2 made rather than an omission.
 *
 * Both fields used to be composed here, in English, by a helper with no
 * translator in scope: `Week of {date}` and `Q3 2026`. The obvious repair —
 * thread a required `t` into `buildBoardTimelineScale`, the shape
 * `boardViewLabel(t, …)` settled at `view-state.ts:127` — was rejected on a
 * grep: **only `BoardTimelineView.tsx` ever read either field.**
 * `BoardFace.tsx:1969`, `board.composition.test.tsx` and
 * `board-timeline.test.ts` all call `buildBoardTimelineScale` for `.from` and
 * `.to` alone, to build a query key. Forcing a translator into a date-range
 * computation for three consumers that never render a label would have handed
 * two non-React test files a standalone translator for nothing.
 *
 * So the scale stays PURE and the copy is resolved at render, through
 * `labelBoardTimelineColumns` below — the same "hold the key, not the
 * sentence" ruling `BoardTimelineView`'s `statusKey` state already carries.
 */
export interface BoardTimelineColumn {
  readonly key: string;
  readonly startKey: string;
  readonly endKey: string;
  readonly isToday: boolean;
}

/** A column with its two names resolved. Produced only by `labelBoardTimelineColumns`. */
export interface BoardTimelineLabelledColumn extends BoardTimelineColumn {
  readonly label: string;
  readonly shortLabel: string;
}

export interface BoardTimelineScale {
  readonly zoom: BoardTimelineZoom;
  readonly anchorDate: Date;
  readonly from: string;
  readonly to: string;
  readonly columns: readonly BoardTimelineColumn[];
}

export interface BoardTimelinePlacedEntry {
  readonly laneId: string;
  readonly entry: TimelineCardEntry;
  readonly startColumnIndex: number;
  readonly span: number;
  readonly startKey: string;
  readonly endKey: string;
  readonly isPoint: boolean;
}

export interface BoardTimelineDatePatch {
  readonly startAt: string | null;
  readonly dueAt: string | null;
}

const DEFAULT_GROUP_BY: TimelineGroupBy = 'none';

export function boardTimelineSearchParams(
  input: BoardTimelineQueryInput,
): URLSearchParams {
  const parsed = boardTimelineQueryObject(input);
  const params = new URLSearchParams();
  params.set('from', parsed.from);
  params.set('to', parsed.to);
  params.set('groupBy', parsed.groupBy);

  for (const member of parsed.members ?? []) params.append('members', member);
  for (const label of parsed.labels ?? []) params.append('labels', label);
  for (const due of parsed.due ?? []) params.append('due', due);
  for (const completion of parsed.completion ?? [])
    params.append('completion', completion);
  if (parsed.activity !== undefined) params.set('activity', parsed.activity);
  if (parsed.keyword !== undefined) params.set('keyword', parsed.keyword);

  return params;
}

export function boardTimelineQueryCacheKey(input: BoardTimelineQueryInput): string {
  return boardTimelineSearchParams(input).toString();
}

export function getBoardTimeline(
  boardId: string,
  input: BoardTimelineQueryInput,
): Promise<BoardTimeline> {
  const search = boardTimelineSearchParams(input).toString();
  return get(
    `/boards/${encodeURIComponent(boardId)}/timeline?${search}`,
    boardTimelineSchema,
  );
}

export function boardTimelineQueryOptions(
  boardId: string,
  input: BoardTimelineQueryInput,
) {
  const cacheKey = boardTimelineQueryCacheKey(input);
  return queryOptions({
    queryKey: queryKeys.boards.timeline(boardId, cacheKey),
    queryFn: () => getBoardTimeline(boardId, input),
  });
}

export function useBoardTimeline(
  boardId: string,
  input: BoardTimelineQueryInput,
  enabled = true,
): UseQueryResult<BoardTimeline, ApiError> {
  const cacheKey = boardTimelineQueryCacheKey(input);
  return useQuery<BoardTimeline, ApiError>({
    queryKey: queryKeys.boards.timeline(boardId, cacheKey),
    queryFn: () => getBoardTimeline(boardId, input),
    enabled,
  });
}

export function buildBoardTimelineScale(options: {
  readonly zoom: BoardTimelineZoom;
  readonly anchorDate: Date | string;
}): BoardTimelineScale {
  const anchor = parseAnchorDate(options.anchorDate);
  const todayKey = calendarDateKey(new Date());
  const firstVisible = timelineScaleStart(anchor, options.zoom);
  const columns = Array.from(
    { length: timelineColumnCount(options.zoom) },
    (_, index): BoardTimelineColumn => {
      const start = addTimelineUnits(firstVisible, options.zoom, index);
      const next = addTimelineUnits(firstVisible, options.zoom, index + 1);
      const end = addCalendarDays(next, -1);
      const startKey = calendarDateKey(start);
      const endKey = calendarDateKey(end);
      return {
        key: `${options.zoom}:${startKey}`,
        startKey,
        endKey,
        isToday: startKey <= todayKey && endKey >= todayKey,
      };
    },
  );

  return {
    zoom: options.zoom,
    anchorDate: anchor,
    from: calendarDateFromKey(columns[0]!.startKey).toISOString(),
    to: addCalendarDays(
      calendarDateFromKey(columns[columns.length - 1]!.endKey),
      1,
    ).toISOString(),
    columns,
  };
}

export function placeBoardTimelineEntries(
  lanes: readonly TimelineLane[],
  columns: readonly BoardTimelineColumn[],
): readonly BoardTimelinePlacedEntry[] {
  const placements: BoardTimelinePlacedEntry[] = [];
  for (const lane of lanes) {
    for (const entry of lane.entries) {
      const range = timelineEntryRange(entry);
      if (range === null) continue;
      const visibleFirst = columns.findIndex((column) => column.endKey >= range.startKey);
      if (visibleFirst === -1) continue;
      let visibleLast = -1;
      for (let index = columns.length - 1; index >= 0; index -= 1) {
        if (columns[index]!.startKey <= range.endKey) {
          visibleLast = index;
          break;
        }
      }
      if (visibleLast === -1 || visibleFirst > visibleLast) continue;
      placements.push({
        laneId: lane.id,
        entry,
        startColumnIndex: visibleFirst,
        span: visibleLast - visibleFirst + 1,
        startKey: columns[visibleFirst]!.startKey,
        endKey: columns[visibleLast]!.endKey,
        isPoint: range.isPoint,
      });
    }
  }
  return placements;
}

export function boardTimelineMovePatch(
  entry: TimelineCardEntry,
  toDateKey: string,
): BoardTimelineDatePatch | null {
  const startKey = entry.startAt === null ? null : timestampDateKey(entry.startAt);
  const dueKey = entry.dueAt === null ? null : timestampDateKey(entry.dueAt);
  const anchorKey = startKey ?? dueKey;
  if (anchorKey === null) return null;
  if (anchorKey === toDateKey) {
    return { startAt: entry.startAt, dueAt: entry.dueAt };
  }

  const dayDelta = diffCalendarDays(anchorKey, toDateKey);
  return {
    startAt:
      entry.startAt === null
        ? null
        : shiftTimestampByCalendarDays(entry.startAt, dayDelta),
    dueAt:
      entry.dueAt === null ? null : shiftTimestampByCalendarDays(entry.dueAt, dayDelta),
  };
}

export function boardTimelineResizePatch(
  entry: TimelineCardEntry,
  edge: 'start' | 'due',
  toDateKey: string,
): BoardTimelineDatePatch | null {
  const startAt =
    edge === 'start'
      ? replaceTimestampDate(entry.startAt ?? entry.dueAt ?? '', toDateKey)
      : entry.startAt;
  const dueAt =
    edge === 'due'
      ? entry.dueAt === null
        ? dueDateForCalendarDate(toDateKey)
        : replaceTimestampDate(entry.dueAt, toDateKey)
      : entry.dueAt;

  const startKey = startAt === null ? null : timestampDateKey(startAt);
  const dueKey = dueAt === null ? null : timestampDateKey(dueAt);
  if (startKey !== null && dueKey !== null && startKey > dueKey) return null;
  return { startAt, dueAt };
}

export function boardTimelineSchedulePatch(
  dateKey: string,
  zoom: BoardTimelineZoom,
): { readonly startAt: string; readonly dueAt: string } {
  const start = calendarDateFromKey(dateKey);
  start.setHours(9, 0, 0, 0);
  const dueKey = scheduleDueDateKey(dateKey, zoom);
  return {
    startAt: start.toISOString(),
    dueAt: dueDateForCalendarDate(dueKey),
  };
}

function boardTimelineQueryObject(input: BoardTimelineQueryInput): BoardTimelineQuery {
  const filter = input.filter ?? EMPTY_BOARD_FILTER;
  const query: Record<string, unknown> = {
    from: input.from,
    to: input.to,
    groupBy: input.groupBy ?? DEFAULT_GROUP_BY,
  };
  const members = normaliseFacetOptions(filter.members);
  const labels = normaliseFacetOptions(filter.labels);
  if (members.length > 0) query.members = members;
  if (labels.length > 0) query.labels = labels;
  if (filter.due.length > 0) query.due = filter.due;
  if (filter.completion.length > 0) query.completion = filter.completion;
  if (filter.activity !== null) query.activity = filter.activity;
  if (filter.keyword.trim() !== '') query.keyword = filter.keyword.trim();
  return boardTimelineQuerySchema.parse(query);
}

function normaliseFacetOptions(values: readonly string[]): string[] {
  return Array.from(
    new Set(
      values.filter(
        (value) => value === NO_FACET_MATCH || ulidSchema.safeParse(value).success,
      ),
    ),
  ).sort();
}

function parseAnchorDate(value: Date | string): Date {
  if (value instanceof Date) {
    return new Date(value.getFullYear(), value.getMonth(), value.getDate());
  }
  return calendarDateFromKey(value);
}

function timelineScaleStart(anchor: Date, zoom: BoardTimelineZoom): Date {
  if (zoom === 'day') return addCalendarDays(anchor, -7);
  if (zoom === 'week') return startOfWeek(addCalendarDays(anchor, -28));
  if (zoom === 'month') return new Date(anchor.getFullYear(), anchor.getMonth() - 5, 1);
  if (zoom === 'quarter') {
    const quarterStartMonth = Math.floor(anchor.getMonth() / 3) * 3;
    return new Date(anchor.getFullYear(), quarterStartMonth - 9, 1);
  }
  return new Date(anchor.getFullYear() - 2, 0, 1);
}

function timelineColumnCount(zoom: BoardTimelineZoom): number {
  if (zoom === 'day') return 21;
  if (zoom === 'week') return 12;
  if (zoom === 'month') return 12;
  if (zoom === 'quarter') return 8;
  return 5;
}

function addTimelineUnits(date: Date, zoom: BoardTimelineZoom, units: number): Date {
  if (zoom === 'day') return addCalendarDays(date, units);
  if (zoom === 'week') return addCalendarDays(date, units * 7);
  if (zoom === 'month') return new Date(date.getFullYear(), date.getMonth() + units, 1);
  if (zoom === 'quarter')
    return new Date(date.getFullYear(), date.getMonth() + units * 3, 1);
  return new Date(date.getFullYear() + units, 0, 1);
}

function startOfWeek(date: Date): Date {
  const start = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const diff = (start.getDay() + 6) % 7;
  start.setDate(start.getDate() - diff);
  return start;
}

/** 1-based, the form every locale's quarter frame slots. */
function quarterOfYear(date: Date): number {
  return Math.floor(date.getMonth() / 3) + 1;
}

/**
 * ⚠ `t` FIRST and REQUIRED, matching `cardDisplayTitle`, `dragInstructions` and
 * `boardViewLabel`. A defaulted translator is the shape that lets one caller
 * fall through to a default that looks correct.
 *
 * ⚠ `startKey`, not a `Date`, and the round trip is exact rather than
 * convenient: every column start is a local midnight built by
 * `new Date(y, m, d)`, so `calendarDateKey` → `calendarDateFromKey` returns the
 * same instant. Taking the key is what lets `BoardTimelineColumn` stay the only
 * thing a caller has to carry.
 *
 * ⚠ ONLY the `week` and `quarter` arms hold a catalogue key. `day`, `month` and
 * `year` delegate to `Intl.DateTimeFormat`, which is already locale-correct —
 * declaring a key for them would AUTHOR copy where the platform authors none,
 * the same ruling that left `boardCalendar.heading` undeclared.
 */
export function timelineColumnLabel(
  t: LocaleFormattingTranslate,
  zoom: BoardTimelineZoom,
  startKey: string,
): string {
  const date = calendarDateFromKey(startKey);
  if (zoom === 'day') return longDateLabel(startKey);
  if (zoom === 'week') {
    return t('boardTimeline.column.week', { date: shortDateLabel(startKey) });
  }
  if (zoom === 'month') {
    return new Intl.DateTimeFormat(undefined, { month: 'long', year: 'numeric' }).format(
      date,
    );
  }
  if (zoom === 'quarter') {
    return t('boardTimeline.column.quarter', {
      quarter: quarterOfYear(date),
      year: date.getFullYear(),
    });
  }
  return String(date.getFullYear());
}

/**
 * ⚠ THE `quarter` ARM IS ROUTED HERE TOO, and the FE-80.1 inventory row said it
 * would not be. The row's premise — that this function "uses
 * `Intl.DateTimeFormat` throughout and is already locale-correct" — holds for
 * `day`, `week`, `month` and `year`, and is FALSE for `quarter`, which was
 * `` `Q${…}` `` with no `Intl` anywhere.
 *
 * Leaving it would have shipped a defect this slice INTRODUCED: at quarter zoom
 * the summary line above the grid would read the translated frame while the
 * `columnheader` row directly under it read `Q1 Q2 Q3 Q4`, mixed-language on one
 * view at one zoom. Correction recorded in the brief and the returns.
 */
export function timelineColumnShortLabel(
  t: LocaleFormattingTranslate,
  zoom: BoardTimelineZoom,
  startKey: string,
): string {
  const date = calendarDateFromKey(startKey);
  if (zoom === 'day') {
    return new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' }).format(
      date,
    );
  }
  if (zoom === 'week') return shortDateLabel(startKey);
  if (zoom === 'month') {
    return new Intl.DateTimeFormat(undefined, { month: 'short' }).format(date);
  }
  if (zoom === 'quarter') {
    return t('boardTimeline.column.quarterShort', { quarter: quarterOfYear(date) });
  }
  return String(date.getFullYear());
}

/**
 * The one call site a view needs: a scale's columns with both names resolved.
 *
 * Kept beside the scale rather than in the view so that the pin has a single
 * seam to assert and so a second timeline surface cannot re-derive the frames.
 */
export function labelBoardTimelineColumns(
  t: LocaleFormattingTranslate,
  scale: BoardTimelineScale,
): readonly BoardTimelineLabelledColumn[] {
  return scale.columns.map((column) => ({
    ...column,
    label: timelineColumnLabel(t, scale.zoom, column.startKey),
    shortLabel: timelineColumnShortLabel(t, scale.zoom, column.startKey),
  }));
}

function timelineEntryRange(entry: TimelineCardEntry): {
  readonly startKey: string;
  readonly endKey: string;
  readonly isPoint: boolean;
} | null {
  const startKey = entry.startAt === null ? null : timestampDateKey(entry.startAt);
  const dueKey = entry.dueAt === null ? null : timestampDateKey(entry.dueAt);
  const firstKey = startKey ?? dueKey;
  const secondKey = dueKey ?? startKey;
  if (firstKey === null || secondKey === null) return null;
  const rangeStart = firstKey <= secondKey ? firstKey : secondKey;
  const rangeEnd = firstKey <= secondKey ? secondKey : firstKey;
  return {
    startKey: rangeStart,
    endKey: rangeEnd,
    isPoint: startKey === null || dueKey === null || rangeStart === rangeEnd,
  };
}

function scheduleDueDateKey(dateKey: string, zoom: BoardTimelineZoom): string {
  const date = calendarDateFromKey(dateKey);
  if (zoom === 'day') return dateKey;
  if (zoom === 'week') return calendarDateKey(addCalendarDays(date, 6));
  if (zoom === 'month') {
    return calendarDateKey(new Date(date.getFullYear(), date.getMonth() + 1, 0));
  }
  if (zoom === 'quarter') {
    const quarterStartMonth = Math.floor(date.getMonth() / 3) * 3;
    return calendarDateKey(new Date(date.getFullYear(), quarterStartMonth + 3, 0));
  }
  return calendarDateKey(new Date(date.getFullYear() + 1, 0, 0));
}

function longDateLabel(dateKey: string): string {
  return new Intl.DateTimeFormat(undefined, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  }).format(calendarDateFromKey(dateKey));
}

function shortDateLabel(dateKey: string): string {
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(calendarDateFromKey(dateKey));
}
